import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "antd";
import dayjs from "dayjs";
import apiClient from "../../../helpers/apiClient";
import {
    getAlreadyPaidAmount,
    getReservationPatchIds,
} from "../utils/reservationHelpers";

const formatMoney = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AddCoverageSummaryCard = ({ reservation, selectedInsuranceId, t }) => {
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(false);
    const [carDetails, setCarDetails] = useState(null);

    const car = reservation?.carSelected || {};
    const carName =
        carDetails?.carName ||
        car?.name ||
        car?.carName ||
        reservation?.carName ||
        "";
    const carImage =
        carDetails?.images?.image1 ||
        carDetails?.images?.image2 ||
        carDetails?.images?.image3 ||
        car?.image ||
        car?.images?.image1 ||
        "";
    const plateNumber = carDetails?.carNumber || car?.carNumber || "";

    const datesLabel = useMemo(() => {
        const pickup = dayjs(reservation?.exactPickupTime || reservation?.pickupDate);
        const dropoff = dayjs(reservation?.exactDropoffTime || reservation?.dropoffDate);
        if (!pickup.isValid() || !dropoff.isValid()) return "";

        if (pickup.isSame(dropoff, "month") && pickup.isSame(dropoff, "year")) {
            return `${pickup.format("MMM D")} - ${dropoff.format("D, YYYY")}`;
        }
        if (pickup.isSame(dropoff, "year")) {
            return `${pickup.format("MMM D")} - ${dropoff.format("MMM D, YYYY")}`;
        }
        return `${pickup.format("MMM D, YYYY")} - ${dropoff.format("MMM D, YYYY")}`;
    }, [reservation]);

    useEffect(() => {
        if (!reservation?.isCarSelected) {
            setCarDetails(null);
            return undefined;
        }

        if (!Array.isArray(reservation?.carLinked) || reservation.carLinked.length === 0) {
            setCarDetails(null);
            return undefined;
        }

        const vin = reservation?.carSelected?.VIN;
        if (!vin) {
            setCarDetails(null);
            return undefined;
        }

        let cancelled = false;

        const fetchCar = async () => {
            try {
                const { data } = await apiClient.get(`/api/car/${vin}`);
                if (!cancelled && data?.success) {
                    setCarDetails(data.data);
                }
            } catch {
                if (!cancelled) {
                    setCarDetails(null);
                }
            }
        };

        fetchCar();

        return () => {
            cancelled = true;
        };
    }, [reservation]);

    const generateInvoice = useCallback(async () => {
        const { category, pickupLocation } = getReservationPatchIds(reservation);
        const carId = reservation?.carSelected?._id || reservation?.carSelected?.id;
        const pickupDay = dayjs(reservation?.exactPickupTime || reservation?.pickupDate);
        const dropoffDay = dayjs(reservation?.exactDropoffTime || reservation?.dropoffDate);
        const existingInsuranceId = reservation?.addonsInsuranceId || "";
        const regularAddons = Array.isArray(reservation?.addons)
            ? reservation.addons.filter((id) => id && id !== existingInsuranceId)
            : [];
        const insuranceId = selectedInsuranceId ? String(selectedInsuranceId) : "";

        if (!pickupDay.isValid() || !dropoffDay.isValid() || !category || !pickupLocation || !carId) {
            setInvoice(null);
            return null;
        }

        setLoading(true);
        try {
            const { data } = await apiClient.post("/api/reservation/invoice-v2", {
                pickupDate: pickupDay.toDate(),
                dropoffDate: dropoffDay.toDate(),
                category,
                location: pickupLocation,
                addonIdList: insuranceId
                    ? regularAddons.filter((id) => String(id) !== insuranceId)
                    : regularAddons,
                carId,
                insuranceId,
            });

            if (data?.success && data?.data?.invoice) {
                const nextInvoice = data.data.invoice;
                const alreadyPaid = getAlreadyPaidAmount(reservation);
                const total = Number(nextInvoice.total || 0);
                const due = Math.max(0, Math.round((total - alreadyPaid) * 100) / 100);
                return { ...nextInvoice, alreadyPaid, due };
            }

            return null;
        } catch {
            return null;
        } finally {
            setLoading(false);
        }
    }, [reservation, selectedInsuranceId]);

    useEffect(() => {
        let cancelled = false;

        const timer = window.setTimeout(async () => {
            const nextInvoice = await generateInvoice();
            if (!cancelled) {
                setInvoice(nextInvoice);
            }
        }, 300);

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [generateInvoice]);

    const charges = Array.isArray(invoice?.charges) ? invoice.charges : [];
    const taxes = Array.isArray(invoice?.taxes) ? invoice.taxes : [];
    const discountAmount = Number(invoice?.discount?.amount || 0);
    const alreadyPaid = Number(invoice?.alreadyPaid || 0);
    const amountDue = Number(invoice?.due ?? invoice?.total ?? 0);

    return (
        <aside className="h-fit overflow-hidden rounded-2xl border border-[#EEF0F6] bg-white shadow-[0_8px_30px_rgba(17,24,39,0.08)] lg:sticky lg:top-24">
            <div className="pointer-events-none select-none bg-[#F6F7FB]">
                {carImage ? (
                    <img src={carImage} alt={carName || "Vehicle"} className="h-44 w-full object-cover" draggable={false} />
                ) : (
                    <div className="flex h-44 w-full items-center justify-center text-sm text-[#98A2B3]">
                        {t("noImage")}
                    </div>
                )}
            </div>

            <div className="space-y-5 p-5">
                <div>
                    <h3 className="fontSemi text-xl capitalize text-[#222222]">
                        {carName || t("selectedVehicle")}
                    </h3>
                    {plateNumber ? (
                        <p className="fontRegular mt-1 text-sm text-[#7F8796]">
                            {t("plateNumber")}: {plateNumber}
                        </p>
                    ) : null}
                </div>

                {datesLabel ? (
                    <div className="border-t border-[#EEF0F6] pt-4">
                        <p className="text-sm text-[#98A2B3]">{t("dates")}</p>
                        <p className="fontRegular mt-1 text-sm font-medium text-[#222222]">{datesLabel}</p>
                    </div>
                ) : null}

                <div className="space-y-3 border-t border-[#EEF0F6] pt-4 text-sm">
                    <p className="fontSemi text-[#222222]">{t("priceDetails")}</p>

                    {loading ? (
                        <div className="space-y-2">
                            <Skeleton.Input active size="small" block />
                            <Skeleton.Input active size="small" block />
                        </div>
                    ) : invoice ? (
                        <>
                            {charges.map((item, index) => (
                                <div key={`charge-${index}`} className="flex items-center justify-between text-[#5B6472]">
                                    <span className="capitalize">
                                        {item?.description}
                                        {item?.unit != null ? ` x ${item.unit}` : ""}
                                    </span>
                                    <span className="font-medium text-[#222222]">${formatMoney(item?.total)}</span>
                                </div>
                            ))}

                            {taxes.map((item, index) => (
                                <div key={`tax-${index}`} className="flex items-center justify-between text-[#5B6472]">
                                    <span className="capitalize">{item?.taxName}</span>
                                    <span className="font-medium text-[#222222]">${formatMoney(item?.total)}</span>
                                </div>
                            ))}

                            {discountAmount > 0 ? (
                                <div className="flex items-center justify-between text-green-600">
                                    <span>{t("discount")}</span>
                                    <span className="font-medium">-${formatMoney(discountAmount)}</span>
                                </div>
                            ) : null}

                            {invoice?.total != null ? (
                                <div className="flex items-center justify-between border-t border-[#EEF0F6] pt-3">
                                    <span className="fontSemi text-[#222222]">{t("total")}</span>
                                    <span className="text-lg font-bold text-[#5D5DF6]">${formatMoney(invoice.total)}</span>
                                </div>
                            ) : null}

                            <div className="flex items-center justify-between text-[#5B6472]">
                                <span>{t("alreadyPaid")}</span>
                                <span className="font-medium">-${formatMoney(alreadyPaid)}</span>
                            </div>

                            <div className="flex items-center justify-between rounded-lg bg-[#F0F7FF] px-3 py-2">
                                <span className="fontSemi text-[#5D5DF6]">{t("amountDue")}</span>
                                <span className="text-lg font-bold text-[#5D5DF6]">${formatMoney(amountDue)}</span>
                            </div>
                        </>
                    ) : (
                        <p className="text-[#98A2B3]">{t("selectInsuranceToPreview")}</p>
                    )}
                </div>
            </div>
        </aside>
    );
};

AddCoverageSummaryCard.propTypes = {
    reservation: PropTypes.object,
    selectedInsuranceId: PropTypes.string,
    t: PropTypes.func.isRequired,
};

export default AddCoverageSummaryCard;
